import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import css from "../style.module.css";
import CompletionCheckMark from './../../Services/CompletionCheckMark';

const QuestionItem = ({
    question, isReadOnly, submitTriggered, highlightedQuestions,
    onPickListClick, onMultiSelectChange, onFileChange, onFileDelete,
    onTextChange, fileName, fileError, deletingFiles, deletedFiles
}) => {
    const shouldHighlight = submitTriggered && highlightedQuestions.has(question.Id);

    // Local state for text input to prevent freezing
    const [localTextValue, setLocalTextValue] = useState(question.pirm_addon__Answer__c || "");

    // Sync local state when question changes from parent
    useEffect(() => {
        if (question.pirm_addon__QuestionType__c === "Free Text") {
            setLocalTextValue(question.pirm_addon__Answer__c || "");
        }
    }, [question.pirm_addon__Answer__c, question.pirm_addon__QuestionType__c]);

    const isChecked = (val) => {
        if (!question.pirm_addon__Answer__c) return false;

        try {
            const answer = String(question.pirm_addon__Answer__c);
            const cleanAnswer = answer.replace(/[\[\]"']/g, '');
            const answerArray = cleanAnswer.split(',').map(s => s.trim());
            return answerArray.includes(val);
        } catch (e) {
            return false;
        }
    };

    const isPicklistChecked = (val) => {
        if (!question.pirm_addon__Answer__c) return false;

        // Remove brackets, quotes and trim whitespace
        const cleanAnswer = String(question.pirm_addon__Answer__c)
            .replace(/[\[\]"']/g, '')
            .trim();
        return cleanAnswer === String(val).trim();
    };

    const sanitizedHtml = DOMPurify.sanitize(question.pirm_addon__Question_Text_Rich__c || question.pirm_addon__Question_Text__c || "");

    return (
        <div className={`d-flex my-4 align-items-start ${shouldHighlight ? css.pulseError : ''}`} data-question-id={question.Id}>
            {!isReadOnly && <CompletionCheckMark isHighlighted={shouldHighlight} />}
            <p className="m-0 me-1">{question.pirm_addon__Question_Order__c}-</p>

            <div className="flex-grow-1" style={{ width: "93%" }}>
                <div className="mb-1" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />

                {/* Picklist (Radio) */}
                {question.pirm_addon__QuestionType__c === "Picklist" && (
                    <div className="ms-2">
                        {JSON.parse(question.pirm_addon__Question__r.pirm_addon__Possible_Responses__c || "[]").map((resp, idx) => (
                            <div className="d-block mt-2" key={idx}>
                                <label className="holdCursor d-flex align-items-center">
                                    <input
                                        type="radio"
                                        className="custom-input"
                                        name={question.Id}
                                        checked={isPicklistChecked(resp.name)}
                                        onChange={() => onPickListClick(question, resp.name)}
                                        disabled={isReadOnly}
                                    />
                                    <span className="custom-checkmark ms-2"></span>
                                    <span className="custom-input-label ms-1">{resp.name}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                )}

                {/* Multi-Select (Checkbox) */}
                {question.pirm_addon__QuestionType__c === "Multi-Select Picklist" && (
                    <div className="ms-2">
                        {JSON.parse(question.pirm_addon__Question__r.pirm_addon__Possible_Responses__c || "[]").map((resp, idx) => (
                            <div className="d-block mt-2" key={idx}>
                                <label className="holdCursor d-flex align-items-center">
                                    <input
                                        type="checkbox"
                                        className="custom-input"
                                        checked={isChecked(resp.name)}
                                        onChange={() => onMultiSelectChange(question, resp.name)}
                                        disabled={isReadOnly}
                                    />
                                    <span className="custom-checkmark ms-2"></span>
                                    <span className="custom-input-label ms-1">{resp.name}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                )}

                {/* Free Text (Input) - NOW WITH LOCAL STATE */}
                {question.pirm_addon__QuestionType__c === "Free Text" && (
                    <input
                        type="text"
                        className={`${css.inputText} border-0 w-100 mt-2`}
                        value={localTextValue}
                        onChange={(e) => {
                            setLocalTextValue(e.target.value); // Update UI immediately
                            onTextChange(question, e.target.value); // Update cache in background
                        }}
                        readOnly={isReadOnly}
                        placeholder="Type your answer..."
                    />
                )}

                {/* File Upload */}
                {question.pirm_addon__QuestionType__c === "Document Upload" && (
                    <div className="mt-2">
                        <input
                            type="file"
                            id={question.Id}
                            style={{ display: "none" }}
                            onChange={(e) => onFileChange(e, question.Id)}
                        />
                        {!isReadOnly && (
                            <div className="d-flex flex-column align-items-start">
                                <div className="d-flex align-items-center">
                                    <label htmlFor={question.Id} className={`btn ${css.btnFiles}`}>Choose File</label>
                                    {!question.ContentDocumentLinks && (
                                        <span className="ms-2 text-muted small">{fileName[question.Id] || "No file chosen"}</span>
                                    )}
                                </div>

                                {fileError && (
                                    <div className="text-danger small mt-1 fw-bold" style={{ fontSize: '0.75rem' }}>
                                        <i className="fas fa-exclamation-circle me-1"></i>
                                        {fileError}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-2">
                            {question?.ContentDocumentLinks?.records?.length > 0 ? (
                                question?.ContentDocumentLinks.records.map((file, idx) => {
                                    const fileId = file.ContentDocument.Id;
                                    const isDeleting = deletingFiles.has(fileId);
                                    const wasDeleted = deletedFiles?.has(fileId);

                                    return (
                                        <div
                                            key={idx}
                                            className={`mt-2 d-flex align-items-center ${isDeleting ? 'opacity-50' : ''} ${wasDeleted ? 'text-success' : ''}`}
                                            style={{
                                                transition: 'all 0.3s ease',
                                                transform: wasDeleted ? 'scale(0.95)' : 'scale(1)'
                                            }}
                                        >
                                            {!isReadOnly && (
                                                <div className="me-2" style={{ minWidth: '20px' }}>
                                                    {isDeleting ? (
                                                        <i className="fas fa-spinner fa-spin text-danger"></i>
                                                    ) : wasDeleted ? (
                                                        <i className="fas fa-check-circle text-success"></i>
                                                    ) : (
                                                        <i
                                                            className={`fa-solid fa-trash fa-fw holdCursor ${css.trash}`}
                                                            onClick={() => onFileDelete(question, fileId)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                opacity: isDeleting ? '0.5' : '1',
                                                                pointerEvents: isDeleting ? 'none' : 'auto'
                                                            }}
                                                            title="Delete file"
                                                        ></i>
                                                    )}
                                                </div>
                                            )}

                                            <span
                                                className={wasDeleted ? 'text-decoration-line-through' : ''}
                                                style={{
                                                    transition: 'all 0.3s ease',
                                                    opacity: isDeleting ? '0.6' : '1'
                                                }}
                                            >
                                                {file.ContentDocument.Title}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="small text-muted">No files available for this question.</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Blocker */}
                {question.pirm_addon__QuestionType__c === "Blocker" && (
                    <div
                        className={`mb-1 ${question.pirm_addon__QuestionType__c === "Blocker" ? "fw-bold text-danger" : ""}`}
                        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                    />
                )}
            </div>
        </div>
    );
};

export default QuestionItem;